import { flatMap, groupBy, uniqueId } from 'lodash';
import * as LDAP from 'src/config/LDAP';
import { TOAST_TIMEOUT } from 'src/config/Toast';
import CapacityUsageService from 'src/services/capacityUsage/capacityUsageService';
import { type SiteJobCapacityUsageData } from 'src/services/capacityUsage/types/siteJobCapacityUsageData';
import { type AustinCase } from 'src/services/incident/types';
import { type AllocatedJobShift, type TaskConflict } from 'src/services/jmr/types';
import { type JobRequirement } from 'src/services/jobsService';
import { MeasureUnitValues } from 'src/services/tasks/constants';
import { type BackgroundFormData } from 'src/store/newJmrPageSlice/types';
import { type Site } from 'src/store/siteSlice';
import type { AppDispatch } from 'src/store/store';
import { createToast } from 'src/store/toastsSlice';
import { type FeatureFlags } from 'src/store/userSlice';

import { removeSpecialCharactersFromString } from './helpers';

// **** Constants **** //

export const RANK_REPRESENTING_WORST_MATCH_FOR_UI = 999;
export const RANK_REPRESENTING_WORST_MATCH_FROM_SERVICE = 0;

export enum JmrFlowTypes {
  NewRestrictions = 'NewRestrictions',
  AlternativePlacementRecommended = 'AlternativePlacementRecommended',
  CreateNewAccommodation = 'CreateNewAccommodation',
  UpdateDates = 'UpdateDates',
  CompleteTransfer = 'CompleteTransfer',
  ProviderPlacedOffWork = 'ProviderPlacedOffWork',
}

export enum JmrPageProgress {
  Background,
  Restrictions,
  JobMatches,
  Review,
  SendForApproval,
}

export enum IdentifyBodyLocationEnum {
  LEFT = 'L',
  RIGHT = 'R',
  BOTH = 'B',
}

export enum JmrJobTypes {
  ProcessPath = 'Process Path',
  TLD = 'TLD Assignment',
}

export enum CaseSources {
  Austin = 'AUSTIN',
  Dali = 'DALI',
  Bali = 'BALI',
}

export enum ConflictColorCode {
  NO_CONFLICTS = 'success',
  IMPERFECT_CONFLICTS = 'theme',
  SAFE_CONFLICTS = 'neutral',
  UNSAFE_CONFLICTS_BUT_MODIFICATIONS_AVAILABLE = 'warning',
  UNSAFE_CONFLICTS_AND_NO_MODIFICATIONS_AVAILABLE = 'error',
}

// **** Types **** //

export interface JmrResult {
  jobId: number;
  rank: number;
  rankingScore: number;
  jobType: string;
  name: string;
  matchedTasks: TaskRestriction[];
  jobRequirements: JobRequirement[];
  allocatedTasks: AllocatedTask[];
  jobCapacity?: number;
  shifts: JmrShift[] | null;
  disable: boolean;
  /** Note: v2 endpoint always returns a (sometimes empty) list */
  conflicts: TaskConflict[];
  maximumHoursAssociateCanDo: number;
  perfectMatch: boolean;
  modificationNotes: string;
  tierTwoPlusJobModified: boolean;
  forTierTwoPlusRoles: boolean;
}

export interface TaskRestriction {
  taskId: number;
  originalValue: string;
  restrictedValue: string;
  taskName: string;
}

interface AllocatedTask {
  taskId: number;
  assignedValue: string | number;
  taskName: string;
}

interface JmrShift {
  shiftId: number;
  shiftCode: string;
  shiftLengthHours: number;
  slots: number;
  totalHours: number;
}

export interface TableDataRow extends Omit<JmrResult, 'shifts'> {
  id: string;
  isChecked: boolean;
  /** The number of hours allocated to this job. Only available if the job has been selected and hours were entered */
  totalHours: string;
  shiftCode?: string;
  shiftId?: number;
}

// **** Functions **** //

/**
 * Get restrictions string for sending emails
 */
export const getRestrictionString = (conflict: TaskConflict): string => {
  const { taskName, restrictedValue, measureUnitType } = conflict;
  if (measureUnitType === MeasureUnitValues.Bool || !+restrictedValue) {
    return `${taskName}: Unable to perform`;
  }
  const unit = restrictedValue === '1' ? 'hour' : 'hours';
  return `${taskName}, no more than ${restrictedValue} ${unit} per day`;
};

export const getRestrictionsForModification = (conflict: TaskConflict): string =>
  `Restriction: ${getRestrictionString(conflict)}`;

export interface ModificationGroup {
  name: string;
  freeFormText: string | null;
  conflicts: TaskConflict[];
}

/**
 * Groups applied modifications by (name, freeFormText) and collects their parent conflicts.
 */
export const getGroupedModifications = (jmrResults: TableDataRow[]): ModificationGroup[] => {
  const modifications = flatMap(
    jmrResults.filter((r) => r.isChecked),
    (result) =>
      flatMap(result.conflicts ?? [], (conflict) =>
        (conflict.appliedModifications ?? []).map((mod) => ({
          name: mod.name,
          freeFormText: mod.freeFormText ?? null,
          conflict,
        })),
      ),
  );

  const groupKey = ({ name, freeFormText }: { name: string; freeFormText: string | null }) =>
    getModificationText(name, freeFormText);

  return Object.values(groupBy(modifications, groupKey)).map((group) => ({
    name: group[0].name,
    freeFormText: group[0].freeFormText,
    conflicts: group.map((g) => g.conflict),
  }));
};

export const getModificationText = (name: string, freeFormText: string | null) =>
  name + (freeFormText ? `: ${freeFormText}` : '');

export const isT2Admin = (ldap: string[]) => ldap.includes(LDAP.RTW_TESTERS_UAT) || ldap.includes(LDAP.ADMIN);

/**
 * Result to table row
 */
export const jmrResultToTableRow = (
  jmrResult: JmrResult,
  capacityUsageMap: Map<number, SiteJobCapacityUsageData>,
  currentPlacementJobShifts: AllocatedJobShift[],
  isJobModificationsEnabled: boolean,
): TableDataRow[] => {
  // Init common data
  const commonData = {
    id: uniqueId(),
    isChecked: false,
    totalHours: '',
    allocatedTasks: [],
    rank: -1,
    conflicts: (jmrResult.conflicts || []).map((c) => ({ ...c, appliedModifications: [] })),
  };
  // If the rank is smaller than worst rank, give it really high number
  // so that it shows up at the end for better UX
  if (+jmrResult.rank > RANK_REPRESENTING_WORST_MATCH_FROM_SERVICE) {
    commonData.rank = +jmrResult.rank;
  } else {
    commonData.rank = RANK_REPRESENTING_WORST_MATCH_FOR_UI;
  }

  // Return results arrays
  if (!!jmrResult.shifts && jmrResult.shifts.length > 0) {
    return jmrResult.shifts.map((shift) => ({
      ...jmrResult,
      ...commonData,
      id: uniqueId(),
      shiftCode: shift.shiftCode,
      shiftId: shift.shiftId,
      jobCapacity: calculateRemainingCapacity(
        jmrResult.jobId,
        shift,
        capacityUsageMap,
        currentPlacementJobShifts,
        isJobModificationsEnabled,
      ),
    }));
  }

  return [
    {
      ...jmrResult,
      ...commonData,
    },
  ];
};

/**
 * Calculate remaining capacity for a shift
 */
const calculateRemainingCapacity = (
  jobId: number,
  shift: JmrShift,
  capacityUsageMap: Map<number, SiteJobCapacityUsageData>,
  currentPlacementJobShifts: AllocatedJobShift[],
  isJobModificationsEnabled: boolean,
) => {
  const shiftsMap = CapacityUsageService.createShiftsMapForJob(jobId, capacityUsageMap);
  let capacityRemaining = shift.totalHours;
  if (shiftsMap.has(shift.shiftId)) {
    capacityRemaining -= shiftsMap.get(shift.shiftId)!.allocatedHours;
  }

  // shifts currently allocated to the associate added back to capacity
  currentPlacementJobShifts
    .filter((placement) => placement.shiftId === shift.shiftId && placement.jobId === jobId)
    .forEach((placement) => (capacityRemaining += placement.allocatedHours));

  return isJobModificationsEnabled ? capacityRemaining : capacityRemaining >= 0 ? capacityRemaining : 0;
};

/**
 * Get number of modified conflicts
 */
export const getNumModifiedConflicts = (jobMatch: TableDataRow) => {
  return jobMatch?.conflicts?.filter(
    (conflict) => conflict.appliedModifications && conflict.appliedModifications.length > 0,
  ).length;
};

/**
 * Find if a job in the table is at capacioty
 */
export const isJobAtCapacity = (row: TableDataRow) => row?.jobCapacity !== undefined && row.jobCapacity <= 0;

/** Find if a job can be modified */
export const canJobBeModified = (jobMatch: TableDataRow) =>
  !!jobMatch.conflicts?.length &&
  jobMatch.conflicts.some((conflict) => conflict.availableModifications?.length) &&
  jobMatch.conflicts.every((c) => c.availableModifications.length > 0 || +c.restrictedValue > 0) &&
  !isJobAtCapacity(jobMatch);

/** Find if a job has been modified */
export const hasJobBeenModified = (jobMatch: TableDataRow) =>
  !!jobMatch.conflicts?.length && jobMatch.conflicts.some((conflict) => conflict.appliedModifications?.length);

/** Find if a job has been modified */
export const hasJobBeenModifiedForAllConflicts = (jobMatch: TableDataRow) =>
  jobMatch.conflicts?.length && jobMatch.conflicts.every((conflict) => conflict.appliedModifications?.length);

/* Create data cy based on job name */
export const createDataCyForJob = (jobName: string) => {
  const name = removeSpecialCharactersFromString(jobName);
  return `new-jmr__results__job-${name}`;
};

/** Given an Austin case number, search for a matching case, validate the user has sufficient permissions, and return
 * new partial BackgroundFormData to pre-populate the associate information page. Pop error toast(s) if any of the
 * preceding steps fail */
// Side note: Austin caseId actually refers to a case identifier used internally by Austin. What we are using is the
// case number. Hence the differing parameter name as there is no impetus to refactor all instances in the code base yet
export const formDataFromAustinCase = (
  caseNumber: string,
  austinCases: AustinCase[],
  allowedSites: Site[],
  dispatch: AppDispatch,
): Partial<BackgroundFormData> => {
  const returnValue: Partial<BackgroundFormData> = {};

  // check results of fetchAustinCases
  const matchingCase = austinCases.find((ac) => ac.caseId === caseNumber);
  if (matchingCase !== undefined) {
    returnValue.caseID = caseNumber;
    returnValue.siteLocation = matchingCase.siteName;
  } else {
    dispatch(
      createToast({
        type: 'error',
        message: `No Austin case found with case number: ${caseNumber}`,
        timeout: TOAST_TIMEOUT,
      }),
    );
    // set site location based on caseId string if possible
    returnValue.siteLocation = caseNumber.split('-')[0];
  }

  // validate user has site permissions for site if one could be derived, populate site object normally set by handleChange form update function

  const siteFromAllowedSites = allowedSites.find((s) => s.site === returnValue.siteLocation);
  if (!siteFromAllowedSites) {
    if (!!returnValue.siteLocation) {
      dispatch(
        createToast({
          type: 'error',
          message: `You are not authorized to create placements at Austin case site: ${returnValue.siteLocation}`,
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
    return {};
  } else {
    returnValue.site = siteFromAllowedSites;
  }
  return returnValue;
};
